"use client"

import { BarsThree, XMark } from "@medusajs/icons"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import SearchModal from "@modules/search/components/search-modal"
import Image from "@modules/common/components/cdn-image"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

type MenuItem = {
  label: string
  url?: string | null
  openInNewTab?: boolean | null
  enabled?: boolean | null
  mobileHidden?: boolean | null
  badge?: string | null
  children?: MenuItem[] | null
}

function ChevronDown({ open }: { open: boolean }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
      className={`h-3.5 w-3.5 shrink-0 transition-transform duration-300 ${open ? "rotate-180 text-[#3D5240]" : "rotate-0"}`}
    >
      <path
        fillRule="evenodd"
        d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function AccordionItem({
  item,
  isOpen,
  onToggle,
  onClose,
}: {
  item: MenuItem
  isOpen: boolean
  onToggle: () => void
  onClose: () => void
}) {
  const visibleChildren = (item.children ?? []).filter(
    (c) => c.enabled !== false && !c.mobileHidden,
  )
  const hasChildren = Boolean(visibleChildren.length)
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <div className="border-b border-[#E8E8E8]">
      <div className="flex items-center">
        {/* Parent link — always navigable */}
        <LocalizedClientLink
          href={item.url || "/"}
          target={item.openInNewTab ? "_blank" : undefined}
          onClick={onClose}
          className="flex flex-1 items-center gap-2 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#181512] transition-colors hover:text-[#3D5240]"
        >
          {item.label}
          {item.badge && (
            <span className="rounded bg-[#3D5240]/25 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#3D5240]">
              {item.badge}
            </span>
          )}
        </LocalizedClientLink>

        {/* Toggle button — only shown when there are children */}
        {hasChildren && (
          <button
            type="button"
            aria-expanded={isOpen}
            aria-label={`${isOpen ? "Collapse" : "Expand"} ${item.label}`}
            onClick={onToggle}
            className="flex h-11 w-11 shrink-0 items-center justify-center text-[#8a7f72] transition-colors hover:text-[#3D5240]"
          >
            <ChevronDown open={isOpen} />
          </button>
        )}
      </div>

      {/* Animated accordion panel */}
      {hasChildren && (
        <div
          ref={contentRef}
          style={{
            maxHeight: isOpen
              ? `${contentRef.current?.scrollHeight ?? 500}px`
              : "0px",
            overflow: "hidden",
            transition: "max-height 0.32s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div className="pb-2 pl-4 pt-1">
            {visibleChildren.map((child) => (
              <LocalizedClientLink
                key={`${child.label}-${child.url || ""}`}
                href={child.url || "/"}
                target={child.openInNewTab ? "_blank" : undefined}
                onClick={onClose}
                className="flex items-center gap-2 py-2.5 text-[11px] font-medium uppercase tracking-[0.16em] text-[#6f665c] transition-colors hover:text-[#3D5240]"
              >
                {child.label}
                {child.badge && (
                  <span className="rounded bg-[#3D5240]/20 px-1 py-0.5 text-[9px] font-bold uppercase text-[#3D5240]">
                    {child.badge}
                  </span>
                )}
              </LocalizedClientLink>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function MobileMenu({
  items,
  logoUrl,
  logoAlt,
  popularSearches,
}: {
  items?: MenuItem[] | null
  logoUrl?: string | null
  logoAlt?: string | null
  popularSearches?: string[]
}) {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  // Track which accordion item is expanded (by index); null = all collapsed
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const close = () => {
    setOpen(false)
    setExpandedIndex(null)
  }

  const handleToggle = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index))
  }

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!open) return

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") close()
    }

    window.addEventListener("keydown", closeOnEscape)

    return () => {
      document.body.style.overflow = originalOverflow
      window.removeEventListener("keydown", closeOnEscape)
    }
  }, [open])

  return (
    <>
      <button
        type="button"
        aria-label="Open navigation menu"
        onClick={() => setOpen(true)}
        className="inline-flex h-11 w-11 items-center justify-center text-[#181512] transition-colors hover:text-[#3D5240]"
      >
        <BarsThree className="h-6 w-6" aria-hidden="true" />
      </button>

      {mounted &&
        open &&
        createPortal(
          <div
            className="fixed inset-0 z-[1000] flex flex-col bg-[#FFFFFF] animate-fade-in-right"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            {/* Header */}
            <div className="grid h-[72px] shrink-0 grid-cols-[44px_1fr_44px] items-center border-b border-[#E5E5E5] px-4">
              <button
                type="button"
                aria-label="Close navigation menu"
                onClick={close}
                className="inline-flex h-11 w-11 items-center justify-center text-[#181512] transition-colors hover:text-[#3D5240]"
              >
                <XMark className="h-6 w-6" />
              </button>

              <LocalizedClientLink
                href="/"
                onClick={close}
                className="inline-flex items-center justify-center"
                aria-label="Home"
              >
                {logoUrl ? (
                  <Image
                    src={logoUrl}
                    alt={logoAlt || "Store"}
                    width={160}
                    height={48}
                    className="max-h-10 w-auto"
                  />
                ) : (
                  <span className="font-serif text-2xl tracking-[0.08em] text-[#181512]">
                    {logoAlt || "Store"}
                  </span>
                )}
              </LocalizedClientLink>

              <SearchModal
                iconOnly
                onNavigate={close}
                popularSearches={popularSearches}
                className="inline-flex h-11 w-11 items-center justify-center justify-self-end text-[#181512] transition-colors hover:text-[#3D5240]"
              />
            </div>

            {/* Scrollable nav body */}
            <nav className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-6">
              <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#8a7f72]">
                Menu
              </p>

              <div className="flex flex-col">
                {(items ?? [])
                  .filter((item) => item.enabled !== false && !item.mobileHidden)
                  .map((item, index) => (
                    <AccordionItem
                      key={`${item.label}-${item.url || ""}`}
                      item={item}
                      isOpen={expandedIndex === index}
                      onToggle={() => handleToggle(index)}
                      onClose={close}
                    />
                  ))}
              </div>
            </nav>
          </div>,
          document.body
        )}
    </>
  )
}
